import React, { useState, useEffect, useRef } from 'react';
import { BusinessCard } from '@/types';
import { QrCode, Mail, Phone, Edit, Trash2, Star, MapPin, Globe, Users, FileText, Eye, MoreVertical, Building2 } from 'lucide-react';
import { apiService } from '@/services/api.service';
import { useAuth } from '@/hooks/useAuth';
import './BusinessCardItem.css';

interface BusinessCardItemProps {
  card: BusinessCard;
  onEdit: (card: BusinessCard) => void;
  onDelete: (card: BusinessCard) => void;
  onSetDefault?: (card: BusinessCard) => void;
  onQrClick?: (card: BusinessCard) => void;
  onViewCard?: (card: BusinessCard) => void;
  showActions?: boolean;
}

const BusinessCardItem: React.FC<BusinessCardItemProps> = ({
  card,
  onEdit,
  onDelete,
  onSetDefault,
  onQrClick,
  onViewCard,
  showActions = true,
}) => {
  const { getAccessToken, account } = useAuth();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>(card.qrCodeUrl || '');
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const primaryEmail = card.emails?.find(e => e.is_primary)?.email || card.emails?.[0]?.email;
  const primaryPhone = card.phones?.find(p => p.is_primary)?.number || card.phones?.[0]?.number;

  useEffect(() => {
    if (!card.qrCodeUrl && !qrCodeUrl && !isGeneratingQr) {
      generateQRCode();
    }
  }, [card.qrCodeUrl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const generateQRCode = async () => {
    if (!account) return;
    
    setIsGeneratingQr(true);
    try {
      const token = await getAccessToken(true);
      
      // Generate QR code (backend reads card data from database)
      const response = await apiService.generateQRCode({
        cardId: card.cardId,
        customColors: {
          primary: '#1E3A8A',
          secondary: '#FFFFFF'
        }
      }, token);
      
      if (response.data?.vCardQR) {
        setQrCodeUrl(response.data.vCardQR);
      }
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    } finally {
      setIsGeneratingQr(false);
    }
  };

  return (
    <div className={`business-card-item ${card.isDefault ? 'is-default' : ''}`}>
      {/* Floating Action Menu */}
      {showActions && (
        <div className="card-actions-menu" ref={dropdownRef}>
          <button 
            className="menu-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
            title="More actions"
          >
            <MoreVertical size={20} />
          </button>
          {showDropdown && (
            <div className="dropdown-menu">
              <button 
                className="dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(card);
                  setShowDropdown(false);
                }}
              >
                <Edit size={16} />
                <span>Edit Card</span>
              </button>
              {!card.isDefault && onSetDefault && (
                <button 
                  className="dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault(card);
                    setShowDropdown(false);
                  }}
                >
                  <Star size={16} />
                  <span>Set as Default</span>
                </button>
              )}
              <button 
                className="dropdown-item delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(card);
                  setShowDropdown(false);
                }}
                disabled={card.isDefault}
              >
                <Trash2 size={16} />
                <span>Delete Card</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Default Badge */}
      {card.isDefault && (
        <div className="default-badge-floating">
          <Star size={14} />
          <span>Default</span>
        </div>
      )}

      {/* Card Content - Clickable */}
      <div 
        className="business-card-content"
        onClick={() => onViewCard?.(card)}
        style={{ cursor: onViewCard ? 'pointer' : 'default' }}
      >
        {/* Modern Card Layout */}
        <div className="card-modern-layout">
          {/* Left Section - Info */}
          <div className="card-info-section">
            {/* Card Type Label */}
            <div className="card-type-label" title={card.cardName}>
              <QrCode size={14} />
              <span className="card-name-ellipsis" title={card.cardName}>{card.cardName}</span>
            </div>

            {/* Main Info */}
            <div className="card-main-info">
              <h3 className="card-display-name">{card.displayName}</h3>
              {card.jobTitle && <p className="card-job-title">{card.jobTitle}</p>}
              {card.companyName && (
                <div className="card-company">
                  <Building2 size={14} />
                  <span>{card.companyName}</span>
                </div>
              )}
            </div>

            {/* Contact Quick Info */}
            <div className="card-contact-quick">
              {card.emails && card.emails.length > 0 && primaryEmail && (
                <div className="contact-quick-item">
                  <div className="contact-icon">
                    <Mail size={16} />
                  </div>
                  <span className="contact-text">{primaryEmail}</span>
                </div>
              )}
              {card.phones && card.phones.length > 0 && primaryPhone && (
                <div className="contact-quick-item">
                  <div className="contact-icon">
                    <Phone size={16} />
                  </div>
                  <span className="contact-text">{primaryPhone}</span>
                </div>
              )}
              {(!card.emails || card.emails.length === 0) && (!card.phones || card.phones.length === 0) && (
                <div className="no-contact-msg">
                  <span>No contact information available</span>
                </div>
              )}
            </div>

            {/* Metadata Pills */}
            {(card.emails?.length > 0 || card.phones?.length > 0 || card.addresses?.length > 0 || 
              card.website || card.socialProfiles?.length > 0 || card.notes) && (
              <div className="card-metadata-pills">
                {card.emails && card.emails.length > 0 && (
                  <div className="pill" title={`${card.emails.length} email(s)`}>
                    <Mail size={11} />
                    <span>{card.emails.length}</span>
                  </div>
                )}
                {card.phones && card.phones.length > 0 && (
                  <div className="pill" title={`${card.phones.length} phone(s)`}>
                    <Phone size={11} />
                    <span>{card.phones.length}</span>
                  </div>
                )}
                {card.addresses && card.addresses.length > 0 && (
                  <div className="pill" title={`${card.addresses.length} address(es)`}>
                    <MapPin size={11} />
                    <span>{card.addresses.length}</span>
                  </div>
                )}
                {card.website && (
                  <div className="pill" title="Website">
                    <Globe size={11} />
                  </div>
                )}
                {card.socialProfiles && card.socialProfiles.length > 0 && (
                  <div className="pill" title={`${card.socialProfiles.length} social profile(s)`}>
                    <Users size={11} />
                    <span>{card.socialProfiles.length}</span>
                  </div>
                )}
                {card.notes && (
                  <div className="pill" title="Has notes">
                    <FileText size={11} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Section - QR Code */}
          <div className="card-qr-section-modern">
            <div className="qr-container">
              {qrCodeUrl ? (
                <div className="qr-image-wrapper">
                  <img 
                    src={qrCodeUrl} 
                    alt={`${card.cardName} QR Code`}
                    className="card-qr-image-modern"
                    onClick={(e) => {
                      e.stopPropagation();
                      onQrClick?.({ ...card, qrCodeUrl });
                    }}
                  />
                  <div className="qr-scan-hint">
                    <QrCode size={12} />
                    <span>Tap to enlarge</span>
                  </div>
                </div>
              ) : isGeneratingQr ? (
                <div className="qr-loading-modern">
                  <QrCode size={28} className="qr-loading-spin" />
                  <span>Generating...</span>
                </div>
              ) : (
                <div 
                  className="qr-generate-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    generateQRCode();
                  }}
                >
                  <QrCode size={28} />
                  <span>Generate QR</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardItem;
